import React, { useEffect, useId, useRef, useState } from 'react';
import './EventSearchSelect.css';

// Выпадающий список событий с поиском по названию: событий набирается несколько десятков,
// и в нативном <select> нужное приходится искать глазами. Заодно нативный список не дает
// покрасить пункты в Safari — здесь статус видно во всех браузерах.
//
// options: [{ id, name, status }], status используется только для подсветки

// Тач-устройство: на нем фокус в поле поднимает экранную клавиатуру,
// которая закрывает половину списка
const isCoarsePointer = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

const EventSearchSelect = ({
  options,
  value,
  onChange,
  disabled = false,
  placeholder = '',
  emptyText = ''
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const highlightedRef = useRef(null);
  const listId = useId();

  const selected = options.find(option => String(option.id) === String(value));

  const normalizedQuery = query.trim().toLowerCase();
  const filtered = normalizedQuery
    ? options.filter(option => option.name.toLowerCase().includes(normalizedQuery))
    : options;

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const openList = () => {
    if (disabled) return;
    setQuery('');
    // Открываем список на выбранном событии, чтобы стрелками идти от него
    setHighlighted(Math.max(0, options.findIndex(option => String(option.id) === String(value))));
    setOpen(true);
  };

  const select = (option) => {
    onChange(option.id);
    close();
    inputRef.current?.blur();
  };

  // Клик мимо списка возвращает поле к выбранному событию, а не к тексту поиска
  useEffect(() => {
    if (!open) return undefined;

    const handleOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        close();
      }
    };

    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  useEffect(() => {
    if (open) {
      highlightedRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlighted, open]);

  // На тач-устройствах первое касание только раскрывает список: preventDefault не дает
  // полю получить фокус, поэтому клавиатура не выскакивает. Поиск включается повторным
  // касанием по уже открытому полю
  const handleInputMouseDown = (event) => {
    if (open) return;
    if (isCoarsePointer()) event.preventDefault();
    openList();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();

      if (!open) {
        openList();
        return;
      }

      if (filtered.length === 0) return;

      const step = event.key === 'ArrowDown' ? 1 : -1;
      setHighlighted(prev => (prev + step + filtered.length) % filtered.length);
      return;
    }

    if (event.key === 'Enter') {
      if (!open) return;
      event.preventDefault();
      if (filtered[highlighted]) select(filtered[highlighted]);
      return;
    }

    if (event.key === 'Escape' && open) {
      event.preventDefault();
      close();
    }
  };

  return (
    <div
      className={`event-search-select ${disabled ? 'event-search-select-disabled' : ''}`}
      ref={containerRef}
    >
      <input
        ref={inputRef}
        type="text"
        className="event-search-select-input"
        value={open ? query : (selected?.name || '')}
        placeholder={open ? (selected?.name || placeholder) : placeholder}
        onChange={(e) => {
          setQuery(e.target.value);
          setHighlighted(0);
          if (!open) setOpen(true);
        }}
        onMouseDown={handleInputMouseDown}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
      />

      {/* Стрелка — единственный переключатель: клик по самому полю только открывает список,
          чтобы в набранном запросе можно было поправить букву, не закрыв его */}
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        disabled={disabled}
        className={`event-search-select-arrow ${open ? 'event-search-select-arrow-open' : ''}`}
        onMouseDown={(e) => {
          e.preventDefault();
          if (open) {
            close();
          } else {
            openList();
            // Фокус — только для мыши и клавиатуры: на тач-устройстве он вызовет клавиатуру
            if (!isCoarsePointer()) inputRef.current?.focus();
          }
        }}
      >
        ▼
      </button>

      {open && (
        <ul id={listId} className="event-search-select-list" role="listbox">
          {filtered.length === 0 && (
            <li className="event-search-select-empty">{emptyText}</li>
          )}

          {filtered.map((option, index) => (
            <li
              key={option.id}
              ref={index === highlighted ? highlightedRef : null}
              role="option"
              aria-selected={String(option.id) === String(value)}
              className={[
                'event-search-select-option',
                option.status ? `event-search-select-option-${option.status}` : '',
                index === highlighted ? 'event-search-select-option-highlighted' : '',
                String(option.id) === String(value) ? 'event-search-select-option-selected' : ''
              ].filter(Boolean).join(' ')}
              onMouseEnter={() => setHighlighted(index)}
              onMouseDown={(e) => {
                // mousedown, а не click: иначе обработчик клика мимо успеет закрыть список
                e.preventDefault();
                select(option);
              }}
            >
              {option.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default EventSearchSelect;
